import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, IconButton, CircularProgress, Chip,
  TextField, Button, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { Home, SwapHoriz, AssignmentReturn } from '@mui/icons-material';
import axios from 'axios';
import Toast from './Toast';

const API = process.env.REACT_APP_API_URL;
const emptyIssue = { barcode: '', borrowerType: 'Learner', borrowerRef: '', dueDate: '', academicYear: String(new Date().getFullYear()), term: '' };

export default function BookLoansPage() {
  const navigate = useNavigate();
  const [loans, setLoans] = useState([]);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [loading, setLoading] = useState(true);
  const [issue, setIssue] = useState(emptyIssue);
  const [returnDlg, setReturnDlg] = useState(null); // loan
  const [outcome, setOutcome] = useState('Returned');
  const [statusFilter, setStatusFilter] = useState('Assigned');

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const load = async () => {
    try {
      const q = statusFilter ? `?status=${statusFilter}` : '';
      const { data } = await axios.get(`${API}/api/library/loans${q}`, { headers });
      setLoans(data); setError('');
    } catch (err) { setError(err.response?.data?.error || 'Failed to load loans'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line

  const doIssue = async () => {
    if (!issue.barcode.trim() || !issue.borrowerRef.trim()) return setError('Barcode and borrower are required');
    try {
      await axios.post(`${API}/api/library/loans`, {
        ...issue, term: issue.term ? Number(issue.term) : undefined,
      }, { headers });
      setIssue(emptyIssue); setOk('Book issued'); setError(''); load();
    } catch (err) { setError(err.response?.data?.error || 'Failed to issue'); }
  };

  const doReturn = async () => {
    try {
      await axios.put(`${API}/api/library/loans/${returnDlg.loanNo}/return`, { outcome }, { headers });
      setReturnDlg(null); setOutcome('Returned'); setOk('Recorded'); load();
    } catch (err) { setReturnDlg(null); setError(err.response?.data?.error || 'Failed'); }
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center"><SwapHoriz sx={{ mr: 1, color: '#4527a0' }} /><Typography variant="h5" sx={{ color: '#4527a0' }}>Issue / Return</Typography></Box>
        <IconButton onClick={() => navigate('/library')} title="Library Dashboard"><Home /></IconButton>
      </Box>
      <Toast error={error} success={ok} onClose={(k) => k === 'error' ? setError('') : setOk('')} />

      <Card elevation={2} sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Issue a copy</Typography>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <TextField label="Copy barcode" size="small" value={issue.barcode} onChange={e => setIssue({ ...issue, barcode: e.target.value })} />
            <TextField select label="Borrower type" size="small" sx={{ minWidth: 130 }} value={issue.borrowerType} onChange={e => setIssue({ ...issue, borrowerType: e.target.value })}>
              <MenuItem value="Learner">Learner</MenuItem>
              <MenuItem value="Trainer">Trainer</MenuItem>
            </TextField>
            <TextField label={issue.borrowerType === 'Learner' ? 'Adm No' : 'Employee No'} size="small" value={issue.borrowerRef} onChange={e => setIssue({ ...issue, borrowerRef: e.target.value })} />
            <TextField label="Due date" type="date" size="small" InputLabelProps={{ shrink: true }} value={issue.dueDate} onChange={e => setIssue({ ...issue, dueDate: e.target.value })} />
            <TextField label="Year" size="small" sx={{ width: 90 }} value={issue.academicYear} onChange={e => setIssue({ ...issue, academicYear: e.target.value })} />
            <TextField label="Term" size="small" sx={{ width: 80 }} value={issue.term} onChange={e => setIssue({ ...issue, term: e.target.value })} />
            <Button variant="contained" onClick={doIssue}>Issue</Button>
          </Box>
        </CardContent>
      </Card>

      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Loans</Typography>
            <TextField select label="Status" size="small" sx={{ minWidth: 140 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Assigned">Assigned</MenuItem>
              <MenuItem value="Returned">Returned</MenuItem>
              <MenuItem value="Lost">Lost</MenuItem>
              <MenuItem value="Damaged">Damaged</MenuItem>
            </TextField>
          </Box>
          {loading ? <Box textAlign="center" my={2}><CircularProgress /></Box> : loans.length === 0 ? <Typography color="text.secondary">No loans.</Typography> : (
            <Table size="small">
              <TableHead><TableRow>
                <TableCell><strong>Loan</strong></TableCell><TableCell><strong>Book</strong></TableCell><TableCell><strong>Barcode</strong></TableCell>
                <TableCell><strong>Borrower</strong></TableCell><TableCell><strong>Issued</strong></TableCell><TableCell><strong>Due</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell><TableCell></TableCell>
              </TableRow></TableHead>
              <TableBody>
                {loans.map(l => (
                  <TableRow key={l.loanNo} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{l.loanNo}</TableCell>
                    <TableCell>{l.bookTitle}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{l.barcode}</TableCell>
                    <TableCell>{l.borrowerName || l.borrowerRef} <Chip label={l.borrowerType} size="small" variant="outlined" sx={{ ml: 0.5 }} /></TableCell>
                    <TableCell>{l.issueDate ? new Date(l.issueDate).toLocaleDateString() : ''}</TableCell>
                    <TableCell>{l.dueDate ? new Date(l.dueDate).toLocaleDateString() : ''}</TableCell>
                    <TableCell><Chip label={l.status} size="small" color={l.status === 'Assigned' ? 'info' : l.status === 'Returned' ? 'success' : 'error'} variant="outlined" /></TableCell>
                    <TableCell>{l.status === 'Assigned' && <Button size="small" startIcon={<AssignmentReturn />} onClick={() => { setReturnDlg(l); setOutcome('Returned'); }}>Return</Button>}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(returnDlg)} onClose={() => setReturnDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Return — {returnDlg?.bookTitle}</DialogTitle>
        <DialogContent>
          <TextField select label="Outcome" fullWidth size="small" sx={{ mt: 1 }} value={outcome} onChange={e => setOutcome(e.target.value)}>
            <MenuItem value="Returned">Returned</MenuItem>
            <MenuItem value="Damaged">Returned — Damaged</MenuItem>
            <MenuItem value="Lost">Lost</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions><Button onClick={() => setReturnDlg(null)}>Cancel</Button><Button variant="contained" onClick={doReturn}>Confirm</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
